import { __read, __spreadArray } from "tslib";
import { ActionManagerEventType, } from '@office-start/action-types';
import { useCapabilities } from '@office-start/capabilities-service';
import { useOfficeComponentContext } from '@office-start/component-context-service';
import { useSharedContext } from '@office-start/context-service';
import { useMyDriveInfo } from '@office-start/graph-service';
import { useIdentity } from '@office-start/identity-service';
import { useLocStrings } from '@office-start/loc-service';
import { useMruConfig } from '@office-start/mru-config-service';
import { useIsDarkMode } from '@office-start/theming-service';
import { useUserEntitlements } from '@office-start/user-entitlements-service';
import { useProvisionedWorkloads } from '@office-start/workloads-service';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getActionControls, getActionsSystemConfig, } from '../util/systemActionHelper';
import { getExtraOptionsFromEnvironment } from './../actionExecutor';
import { useContentActionsGatesEnabled } from './useContentActionsEnabled';
/**
 * @description Gather all of the state needed to register actions.
 * This includes loc strings, licenses, capabilities, feature gates, as well as installed applications.
 *
 * @returns [DocumentActionsMenuResources | undefined (loc strings), ISystemConfig | undefined]
 */
export var useHubActionsConfig = function () {
    var _a, _b;
    var componentContext = useOfficeComponentContext().data;
    var capabilities = useCapabilities().data;
    var locStrings = useLocStrings().data;
    var userEntitlementsResult = useUserEntitlements().data;
    var myDriveInfoQuery = useMyDriveInfo();
    // ⚙️ Feature gates for actions
    var actionsGates = useContentActionsGatesEnabled();
    return useMemo(function () {
        var _a, _b;
        // Do we have all the things we need to register actions?
        if (!componentContext ||
            !actionsGates ||
            !capabilities ||
            !locStrings ||
            !(userEntitlementsResult === null || userEntitlementsResult === void 0 ? void 0 : userEntitlementsResult.result) ||
            myDriveInfoQuery.isLoading ||
            myDriveInfoQuery.isInitialData) {
            return [undefined, undefined, undefined, undefined];
        }
        var systemConfig = getActionsSystemConfig({
            actionsGates: actionsGates,
            capabilities: capabilities,
            componentContext: componentContext,
            userDriveId: (_b = (_a = myDriveInfoQuery.data) === null || _a === void 0 ? void 0 : _a.result) === null || _b === void 0 ? void 0 : _b.driveId,
            userEntitlements: userEntitlementsResult.result,
        });
        return [
            locStrings.documentActionsMenuResources,
            locStrings.documentActionsToastResources,
            // TODO: Move this to a dialog component.
            locStrings.actionsInContextDialogResources,
            systemConfig,
        ];
    }, [
        componentContext,
        actionsGates,
        capabilities,
        locStrings,
        userEntitlementsResult === null || userEntitlementsResult === void 0 ? void 0 : userEntitlementsResult.result,
        myDriveInfoQuery.isLoading,
        myDriveInfoQuery.isInitialData,
        (_b = (_a = myDriveInfoQuery.data) === null || _a === void 0 ? void 0 : _a.result) === null || _b === void 0 ? void 0 : _b.driveId,
    ]);
};
/**
 * Accumulate all of the state needed to process or execute actions
 * @returns
 */
export var useActionExecutorOptions = function () {
    // Office context
    var componentContext = useOfficeComponentContext().data;
    var identity = useIdentity().data;
    var actionsGates = useContentActionsGatesEnabled();
    var mruConfig = useMruConfig().data;
    var sharedContext = useSharedContext().data;
    var darkMode = useIsDarkMode();
    var workloads = useProvisionedWorkloads();
    return useMemo(function () {
        // Do we have all the things we need to execute actions? If not, bail
        if (!actionsGates ||
            !mruConfig ||
            !componentContext ||
            !identity ||
            !sharedContext ||
            !workloads) {
            return undefined;
        }
        return getExtraOptionsFromEnvironment({
            componentContext: componentContext,
            isDarkMode: function () { return !!darkMode; },
            isRecommendedFilesActionsApiEnabled: function () {
                return !!actionsGates.actionActivityEnabled;
            },
            identity: identity,
            odspShareApiEndPointBase: mruConfig.shareApiEndpoint,
            sharedContext: sharedContext,
            workloads: workloads,
            useCompliantShareApi: actionsGates.compliantShareApiEnabled,
            shareUIPosition: (componentContext === null || componentContext === void 0 ? void 0 : componentContext.workload) == 'officehomereact'
                ? 'absolute'
                : 'fixed',
            tokenProviderEnabled: actionsGates.shareTokenClaimsEnabled,
        });
    }, [
        actionsGates,
        componentContext,
        identity,
        mruConfig,
        sharedContext,
        darkMode,
        workloads,
    ]);
};
export var useActionControls = function () {
    var _a = __read(useHubActionsConfig(), 4), documentActionsMenuResources = _a[0], documentActionsToastResources = _a[1], actionsInContextDialogResources = _a[2], hubActionsConfig = _a[3];
    var extraOptions = useActionExecutorOptions();
    return useMemo(function () {
        // Otherwise, wait for config and make sure we have one
        if (!documentActionsMenuResources ||
            !documentActionsToastResources ||
            !actionsInContextDialogResources ||
            !hubActionsConfig ||
            !extraOptions) {
            return undefined;
        }
        var actionControls = getActionControls(actionsInContextDialogResources, documentActionsMenuResources, documentActionsToastResources, hubActionsConfig, extraOptions);
        if (!actionControls ||
            !actionControls.actionManager ||
            !actionControls.actionExecutor) {
            return undefined;
        }
        return actionControls;
    }, [
        documentActionsMenuResources,
        documentActionsToastResources,
        actionsInContextDialogResources,
        hubActionsConfig,
        extraOptions,
    ]);
};
export var useContentActions = function (actionManager) {
    var _a = __read(useState(), 2), contentActions = _a[0], setContentActions = _a[1];
    var actionsUpdatedCallback = useCallback(function () {
        if (actionManager) {
            setContentActions(__spreadArray([], __read(actionManager.getContentActionsMetadata()), false));
        }
    }, [actionManager]);
    // When new actions are added/removed/updated, update the hook 🪝
    useEffect(function () {
        if (actionManager) {
            actionManager.on(ActionManagerEventType.ActionsUpdated, actionsUpdatedCallback);
            // Get initial set of actions on mount
            actionsUpdatedCallback();
        }
        // Clean up...
        return function () {
            if (actionManager) {
                actionManager.removeListener(ActionManagerEventType.ActionsUpdated, actionsUpdatedCallback);
            }
        };
    }, [actionManager, actionsUpdatedCallback]);
    return contentActions;
};
/**
 * Rather than try to get React Context / Actions manager integrated into OfficeHome, use a singleton hook for all actions.
 * This should ensure that the same instance is returned wherever this is called.
 */
export var useActions = function () {
    var actionsGates = useContentActionsGatesEnabled();
    var actionControls = useActionControls();
    var actionManager = actionControls === null || actionControls === void 0 ? void 0 : actionControls.actionManager;
    var actionExecutor = actionControls === null || actionControls === void 0 ? void 0 : actionControls.actionExecutor;
    var contentActions = useContentActions(actionManager);
    return useMemo(function () {
        if (actionsGates === undefined) {
            return {
                actionsEnabled: false,
                actionManager: actionManager,
            };
        }
        return {
            actionsEnabled: actionsGates.contentActionsEnabled,
            actionManager: actionManager,
            actionExecutor: actionExecutor,
            contentActions: contentActions,
        };
    }, [actionExecutor, actionManager, actionsGates, contentActions]);
};
//# sourceMappingURL=actionManagerHooks.js.map